import React, { useState, useRef, useEffect } from 'react'
import { Filter, ChevronDown } from 'lucide-react'

export type SectionFilterState = {
  companies: boolean
  company_groups: boolean
  users: boolean
  shifts: boolean
  work_items: boolean
  payments: boolean
  positions: boolean
  routes: boolean
}

type FilterDropdownProps = {
  enabledSections: SectionFilterState
  onSectionToggle: (section: keyof SectionFilterState, enabled: boolean) => void
}

export function FilterDropdown({
  enabledSections,
  onSectionToggle,
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const sectionLabels: Record<keyof SectionFilterState, string> = {
    companies: 'Companies',
    company_groups: 'Company Groups',
    users: 'Users',
    shifts: 'Shifts',
    work_items: 'Work Items',
    payments: 'Payments',
    positions: 'Positions',
    routes: 'Routes',
  }

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'relative',
        flexShrink: 0,
      }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          backgroundColor: 'white',
          color: '#374151',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f9fafb'
          e.currentTarget.style.borderColor = '#9ca3af'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'white'
          e.currentTarget.style.borderColor = '#d1d5db'
        }}
      >
        <Filter size={16} />
        <span>Filter</span>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </button>
      {isOpen ? (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            padding: '8px',
            minWidth: '200px',
            zIndex: 100001,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#6b7280',
              padding: '4px 8px',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Sections
          </div>
          {Object.entries(enabledSections).map(([key, enabled]) => (
            <label
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 8px',
                cursor: 'pointer',
                borderRadius: '4px',
                fontSize: '14px',
                color: '#111827',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => {
                  e.stopPropagation()
                  onSectionToggle(
                    key as keyof SectionFilterState,
                    e.target.checked
                  )
                }}
                style={{
                  cursor: 'pointer',
                  width: '16px',
                  height: '16px',
                }}
              />
              <span>{sectionLabels[key as keyof SectionFilterState]}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  )
}
